import React, { useEffect } from 'react';
import { useIsFetching } from 'react-query';
import { Select, Tooltip, Icon } from 'antd';
import {
  shutdownCitiesQuery,
  shutdownInitiatorsQuery,
  shutdownNotificationsQuery,
  shutdownCompaniesQuery,
} from '../../../../../../shared/api/queries';
import Field, { fieldTypes } from '../../../../../../shared/ui/Field';
import { fieldNames } from '../../../config';
import {
  shutdownEmitter,
  shutdownEvtNames,
} from '../../../../../../shared/constants';
import { useNewCompanyValueSubscription } from '../../../lib/subscriptions';

const { useShutdownCities } = shutdownCitiesQuery;
const { Option } = Select;
const fieldName = fieldNames.city;
const companyFieldName = fieldNames.company;

const { key: initiatorsKey } = shutdownInitiatorsQuery;
const { key: notifyToKey } = shutdownNotificationsQuery;
const { key: companiesKey } = shutdownCompaniesQuery;

const useIsDataFetching = () => {
  const isInitiatorsFetching = useIsFetching(initiatorsKey().filter(Boolean));
  const isNotifyToFetching = useIsFetching(notifyToKey().filter(Boolean));
  const isCompaniesFetching = useIsFetching(companiesKey().filter(Boolean));

  const isFetching = [
    isInitiatorsFetching,
    isNotifyToFetching,
    isCompaniesFetching,
  ].some(Boolean);

  return isFetching;
};

const getCityValue = (value, data) => {
  if (value === undefined || value === null) return value;
  const item = data.find((i) => i.id === value);
  return item ? value : undefined;
};

const newCityValueEmit = (value) =>
  shutdownEmitter.emit(shutdownEvtNames.newCityValue, value);

const CitiesField = ({ form }) => {
  const companyDependentData = useNewCompanyValueSubscription();

  const companyId = companyDependentData?.[companyFieldName];

  const { data = [], isFetching: isCitiesFetching } = useShutdownCities(
    companyId,
    {
      staleTime: Infinity,
      enabled: !!companyId,
    }
  );

  const isDataFetching = useIsDataFetching();

  useEffect(() => {
    if (data?.length) {
      const evtName = shutdownEvtNames.setDraftCityValue;

      const handler = (values) => {
        const city = getCityValue(values[fieldName], data);
        form.setFieldsValue({ [fieldName]: city });
        const newValues = { ...values, [fieldName]: city };
        newCityValueEmit(newValues);
      };

      shutdownEmitter.on(evtName, handler);

      return () => shutdownEmitter.off(evtName, handler);
    }
  }, [data, companyDependentData]);

  // const value = form.getFieldValue(fieldName);
  // const hidden = !data || Boolean(data.length === 1 && value);
  const loading = Boolean(isCitiesFetching || isDataFetching);
  const disabled = Boolean(loading || !companyId);

  return (
    <Field
      fieldType={fieldTypes.select}
      // hidden={hidden}
      form={form}
      name={fieldName}
      label={
        <span>
          Город&nbsp;
          <Tooltip title="На основе города формируются списки инициаторов, адресов и тех, кому отправить уведомления">
            <Icon
              type="info-circle"
              style={{ fontSize: '16px' }}
            />
          </Tooltip>
        </span>
      }
      decoratorOptions={{
        rules: [
          {
            required: true,
            message: 'Укажите город',
          },
        ],
      }}
      fieldProps={{
        placeholder: 'Выберите город',
        loading: loading,
        disabled: disabled,
        showSearch: true,
        optionFilterProp: 'searchtext',
        onChange: (value) => newCityValueEmit({ [fieldName]: value }),
      }}
      items={data}
      renderOption={(item) => (
        <Option
          key={item.id}
          value={item.id}
          searchtext={item.name}
        >
          {item.name}
        </Option>
      )}
    />
  );
};

export default CitiesField;
